import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Typography, Box, TextField, CircularProgress } from '@mui/material'
import { Alert } from '@material-ui/lab'
import { navigate } from '@reach/router'
import { getUserContainer } from '../../container/user-module'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { emptyUserDTO, UserDTO } from '../../modules/users/models/User'
import { UpdatePasswordDTO } from '../../modules/users/models/UpdatePasswordDTO'
import { IStatusService } from '../../common/status/StatusService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import AvatarProfile from '../../components/avatar-profile/AvatarProfile'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import styles from './Editor.module.css'
import { ROUTE_NOTIFICATIONS } from '../../routes/routes-constants'
import { PageLayout } from 'pages/layout'

const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export function Editor() {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const { canEditOwnData, canChangeOwnPassword } = useUserRoles(loggedUser)

  const [userToEdit, setUserToEdit] = useState<UserDTO>(emptyUserDTO())
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [message, setMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const isFamiliar = (user: UserDTO): boolean => {
    return user.roles?.includes('FAMILIAR') || user.roles?.includes('familiar') || false
  }

  const getUserRoleText = (user: UserDTO): string => {
    if (user.isPatient) return t('patient')
    if (isFamiliar(user)) return t('familiar')
    return t('doctor')
  }

  useEffect(() => {
    // Sin id no hay nada que pedir: con '' se llama a /users/users/ y el
    // backend responde 500.
    if (!loggedUser?.id) return
    userService.getByID(loggedUser.id).subscribe((res) => {
      if (res) {
        const dto = res.toDTO()
        setUserToEdit(dto)
        setIsLoading(false)
      } else {
        setIsLoading(false)
      }
    })
  }, [])

  useEffect(() => {
    setMessage('')
  }, [userToEdit])

  const handleInput = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setUserToEdit(Object.assign({ ...userToEdit }, { [e.target.name]: e.target.value }))

  const goHome = () => navigate(ROUTE_NOTIFICATIONS)

  const validatePassword = () => {
    if (!newPassword && !repeatPassword) {
      setErrorMessage(t('voidFields'))
      return false
    }

    if (newPassword !== repeatPassword) {
      setErrorMessage('passNotEquals')
      return false
    }

    return true
  }

  const saveData = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const isChangingPassword = oldPassword || newPassword || repeatPassword

    if (isChangingPassword) {
      if (!oldPassword) {
        setErrorMessage('incorrectOldPass')
        return
      }

      if (!validatePassword()) {
        return
      }

      if (loggedUser) {
        setErrorMessage('')
        const newPasswordSend: UpdatePasswordDTO = {
          id: loggedUser.id,
          password: oldPassword,
          newPassword: newPassword,
        }

        userService.changePassword(newPasswordSend).subscribe((res: any) => {
          if (res === 'incorrectPassword') {
            setErrorMessage('Contraseña actual incorrecta')
          } else {
            if (canEditOwnData && !userToEdit.isPatient) {
              userService.update(userToEdit).subscribe(() => {
                statusService.sendStatus({ variant: 'success' })
                setOldPassword('')
                setNewPassword('')
                setRepeatPassword('')
              })
            } else {
              statusService.sendStatus({ variant: 'success' })
              setOldPassword('')
              setNewPassword('')
              setRepeatPassword('')
            }
          }
        })
      }
    } else {
      if (canEditOwnData && !userToEdit.isPatient) {
        userService
          .update(userToEdit)
          .subscribe(() => statusService.sendStatus({ variant: 'success' }))
      }
    }
  }

  return (
    <PageLayout>
      {isLoading ? (
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <form className={styles.form} onSubmit={saveData}>
          <Box className={styles.formInner}>
            <Box className={styles.avatarContainer}>
              <AvatarProfile />
              <Box>
                <Typography
                  className={styles.userName}
                >{`${userToEdit.firstName} ${userToEdit.lastName}`}</Typography>
                <Typography className={styles.roleText}>{getUserRoleText(userToEdit)}</Typography>
              </Box>
            </Box>
            <Box width={'100%'}>
              <Box className={styles.headerContainer}>
                <Typography className={styles.headerTitle}>{t('userData')}</Typography>
              </Box>

              {userToEdit.isPatient ? (
                <Box className={styles.patientGrid}>
                  <Box className={styles.patientCol}>
                    <Box mb={2}>
                      <Typography className={styles.inputTitle}>{t('name')}</Typography>
                      <TextField
                        id={'firstName'}
                        label=""
                        name="firstName"
                        className={styles.textField}
                        value={userToEdit.firstName}
                        onChange={handleInput}
                        type="text"
                        required
                        variant={'outlined'}
                        size={'small'}
                        disabled
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography className={styles.inputTitle}>{t('email')}</Typography>
                      <TextField
                        id={'email'}
                        label={''}
                        name="email"
                        className={styles.textField}
                        value={userToEdit.email}
                        type="email"
                        onChange={handleInput}
                        required
                        variant={'outlined'}
                        size={'small'}
                        disabled
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography className={styles.inputTitle}>{t('role')}</Typography>
                      <TextField
                        id={'role'}
                        label={''}
                        name="role"
                        className={styles.textField}
                        value={t('patient')}
                        type="text"
                        onChange={handleInput}
                        required
                        variant={'outlined'}
                        size={'small'}
                        disabled
                      />
                    </Box>
                  </Box>

                  <Box className={styles.patientCol}>
                    <Box mb={2}>
                      <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
                      <TextField
                        id={'lastName'}
                        label={''}
                        name="lastName"
                        className={styles.textField}
                        value={userToEdit.lastName}
                        onChange={handleInput}
                        type="text"
                        required
                        variant={'outlined'}
                        size={'small'}
                        disabled
                      />
                    </Box>
                    <Box mb={2}>
                      <Typography className={styles.inputTitle}>{t('pathologies')}</Typography>
                      <TextField
                        id={'patologies'}
                        label={''}
                        name="patologies"
                        className={styles.textField}
                        value={
                          userToEdit.patologies && userToEdit.patologies.length > 0
                            ? userToEdit.patologies
                                .map((p) =>
                                  p === 'others' ? userToEdit.otherPatology || t('others') : t(p)
                                )
                                .join(', ')
                            : t('noData')
                        }
                        onChange={handleInput}
                        variant={'outlined'}
                        size={'small'}
                        disabled
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box mb={3} className={styles.fieldsGrid}>
                    <Box className={styles.patientCol}>
                      <Box>
                        <Typography className={styles.inputTitle}>{t('name')}</Typography>
                        <TextField
                          id={'firstName'}
                          label=""
                          name="firstName"
                          className={styles.textField}
                          value={userToEdit.firstName}
                          onChange={handleInput}
                          type="text"
                          required={true}
                          variant={'outlined'}
                          size={'small'}
                          disabled={!canEditOwnData}
                        />
                      </Box>
                      <Box>
                        {' '}
                        <Typography className={styles.inputTitle}>{t('email')}</Typography>
                        <TextField
                          id={'email'}
                          label={''}
                          name="email"
                          className={styles.textField}
                          value={userToEdit.email}
                          type="email"
                          onChange={handleInput}
                          required={true}
                          variant={'outlined'}
                          size={'small'}
                          disabled={true}
                        />
                      </Box>
                    </Box>
                    <Box className={styles.patientCol}>
                      <Box>
                        <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
                        <TextField
                          id={'lastName'}
                          label={''}
                          name="lastName"
                          className={styles.textField}
                          value={userToEdit.lastName}
                          onChange={handleInput}
                          type="text"
                          required={true}
                          variant={'outlined'}
                          size={'small'}
                          disabled={!canEditOwnData}
                        />
                      </Box>
                      <Box>
                        <Typography className={styles.inputTitle}>{t('role')}</Typography>
                        <TextField
                          id={'role'}
                          label={''}
                          name="role"
                          className={styles.textField}
                          value={isFamiliar(userToEdit) ? t('familiar') : t('doctor')}
                          type="text"
                          onChange={handleInput}
                          required={true}
                          variant={'outlined'}
                          size={'small'}
                          disabled={true}
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {canChangeOwnPassword && (
                <>
                  <Box className={styles.headerContainer}>
                    <Typography className={styles.headerTitle}>{t('changePass')}</Typography>
                  </Box>

                  <Box mb={2} className={styles.passwordRowSingle}>
                    <Box>
                      <Typography className={styles.inputTitle}>{t('currentPassword')}</Typography>
                      <TextField
                        id={'currentPass'}
                        label={''}
                        name="currentPass"
                        className={styles.textField}
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        variant={'outlined'}
                        size={'small'}
                      />
                    </Box>
                  </Box>

                  <Box className={styles.passwordRowActions}>
                    <Box>
                      <Typography className={styles.inputTitle}>{t('newPass')}</Typography>
                      <TextField
                        id={'newPass'}
                        label={''}
                        name="newPass"
                        className={styles.textField}
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        variant={'outlined'}
                        size={'small'}
                      />
                    </Box>
                    <Box>
                      <Typography className={styles.inputTitle}>{t('repeatNewPass')}</Typography>
                      <TextField
                        id={'repeatNewPass'}
                        label={''}
                        name="repeatNewPass"
                        className={styles.textField}
                        value={repeatPassword}
                        type="password"
                        onChange={(e) => setRepeatPassword(e.target.value)}
                        variant={'outlined'}
                        size={'small'}
                      />
                    </Box>
                    <Box className={styles.buttonContainer} display="flex" alignSelf="flex-end">
                      <Button
                        className={styles.buttonCancel}
                        type={'button'}
                        onClick={() => goHome()}
                      >
                        {t('cancel')}
                      </Button>
                      <Button className={styles.buttonSubmit} type={'submit'}>
                        {t('save')}
                      </Button>
                    </Box>
                  </Box>
                </>
              )}

              {!canChangeOwnPassword && (
                <Box className={styles.buttonContainer} display="flex">
                  <Button className={styles.buttonCancel} type={'button'} onClick={() => goHome()}>
                    {t('cancel')}
                  </Button>
                  <Button className={styles.buttonSubmit} type={'submit'}>
                    {t('save')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {message && (
            <Box mb={3}>
              <Alert severity="success" key="message" id="message">
                {t(message)}
              </Alert>
            </Box>
          )}
          {errorMessage && (
            <Box mb={3}>
              <Alert severity="error" key="errorMessage" id="errorMessage">
                {t(errorMessage)}
              </Alert>
            </Box>
          )}
        </form>
      )}
    </PageLayout>
  )
}
